import uuidv4 from 'uuid/v4';

import {
  getCustomerData,
  requestPayment,
} from 'paymentIntegrations/adyen/requests/adyenRequests';
import {
  calculateSplitsAmounts,
  // encodeSignatureToBase64,
  offlineListener,
  onlineListener,
  parseQueryString,
} from 'paymentIntegrations/adyen/utils';
import { getCustomers } from 'services/ErplyAPI/customers';
import { float } from 'components/FieldTypes/formatters';
import { FUNC_BUTTONS } from 'paymentIntegrations/types';
import { RootDispatch } from 'reducers';

import { creationOfAdyenPatchscript } from '../printing';
import { getManualEntryModeParams } from '../manualEntryMode';
import { getAdyenTipParams } from '../tip';
import { AdyenCardPaymentHooks } from '../types';

/**
 * Requests a single payment to Adyen terminal and handles the reponse from the same terminal.
 * @param paymentData Data for the payment request.
 * @param hooks Functions to manipulate the card payment UI component.
 */
const adyenPayment = (
  paymentData: { id: any; amount: any; key: any },
  hooks: AdyenCardPaymentHooks,
  splits?: Record<string, any>[],
) => async (dispatch: RootDispatch) => {
  const {
    updateMessage,
    enableButtons,
    beforeDocSave,
    displayWarning,
    adyenConfig,
    printReceipt,
    employeeID,
  } = hooks;
  let customerTransactionID: null | string = null;
  let recurrentCustomerEmail: null | string = null;

  offlineListener(enableButtons, updateMessage);
  onlineListener(enableButtons, updateMessage);
  try {
    if (adyenConfig.adyenRecon === 1) {
      /** *** Get data from recurrent customer ****** */
      updateMessage(
        'Present card to obtain recurrent customer details and start payment.',
      );
      enableButtons([]);
      const decryptedCustomer = (await getCustomerData(
        paymentData,
        adyenConfig,
      )) as any;

      const dataRetrievalResult =
        decryptedCustomer.SaleToPOIResponse.CardAcquisitionResponse.Response
          .Result;
      if (dataRetrievalResult === 'Success') {
        /** *** Obtain transactionID and shopper email **** */
        customerTransactionID =
          decryptedCustomer.SaleToPOIResponse.CardAcquisitionResponse.POIData
            .POITransactionID.TransactionID;
        recurrentCustomerEmail = decodeURIComponent(
          parseQueryString(
            decryptedCustomer.SaleToPOIResponse.CardAcquisitionResponse.Response
              .AdditionalResponse,
          ).shopperEmail,
        );
      }

      /** *** Retrieve matching user from Erply API and set it as the current customer **** */
      if (recurrentCustomerEmail && recurrentCustomerEmail !== undefined) {
        const erplyCustomerResponse = await getCustomers({
          searchEmail: recurrentCustomerEmail,
        });
        let id;
        if (erplyCustomerResponse.length) {
          id = erplyCustomerResponse[0].id;
          if (id && id !== '' && id !== ' ') {
            adyenConfig.setCustomer({ data: erplyCustomerResponse[0] });
          }
        }
      }
    }

    /** *** Perform a payment transaction ****** */
    updateMessage(
      `Performing payment in amount of ${float(paymentData.amount)}`,
    );
    let amountsToPay;

    if (adyenConfig.enableMarketplace === 1) {
      amountsToPay = calculateSplitsAmounts(paymentData.amount, splits);
      if (!amountsToPay.length) {
        throw new Error('Missing Adyen marketplace configuration');
      }
    }
    const manualEntryProps = await dispatch(getManualEntryModeParams());

    const tipProps = await dispatch(getAdyenTipParams());

    const additionalPayload = {
      ...manualEntryProps,
      ...tipProps,
    };

    // If the adyen marketplace has not been configured it would not be posible to perform a payment

    const payment = await requestPayment(
      paymentData,
      adyenConfig,
      customerTransactionID,
      amountsToPay,
      employeeID,
      additionalPayload,
    );

    const { statusMessage } = payment;

    if (
      'transactionStatus' in payment &&
      payment.transactionStatus === 'Success'
    ) {
      const {
        cardType,
        cardNumber,
        referenceNumber,
        approvedAmount,
        additionalData,
        cardHolder,
        authCode,
        // Used for retrieving tip info
        rawResponse,
      } = payment;

      const tipAmount = rawResponse?.PaymentResult?.AmountsResp?.TipAmount;
      await beforeDocSave({
        key: paymentData.key,
        temporaryUUID: paymentData.key,
        type: 'CARD',
        amount: tipAmount
          ? // Approved amount contains tip amount, need to save payment as it was made initially
            (Number(approvedAmount) - tipAmount).toFixed(2)
          : String(approvedAmount),
        cardType,
        cardNumber,
        cardHolder,
        tipAmount,
        // signature: signature ? base64String : null,
        attributes: {
          tipAmount: tipAmount ?? 0,
          refNo: referenceNumber,
          authCode,
        },
        paid: true,
      });
      if (tipAmount) {
        await beforeDocSave({
          key: uuidv4(),
          paid: true,
          type: 'TIP',
          caption: 'TIP (adjustment)',
          amount: -tipAmount,
          cardType,
          attributes: {
            refNo: referenceNumber,
          },
        });
        const uniqID = uuidv4();
        await beforeDocSave({
          key: uniqID,
          temporaryUUID: uniqID,
          paid: true,
          type: 'CARD',
          caption: 'TIP (payment)',
          amount: tipAmount,
          cardType,
          cardNumber,
          cardHolder,
          attributes: {
            cardIsTip: true,
            authCode,
            paymentType: cardType,
            refNo: referenceNumber,
            cardNumber,
          },
        });
      }

      if (adyenConfig.usePOSGeneralPrinter) {
        const parsedAdditionalData = JSON.parse(additionalData);
        const { receipts } = parsedAdditionalData;

        const receiptsPatchscript = creationOfAdyenPatchscript(receipts);

        printReceipt(receiptsPatchscript);
      }

      updateMessage('Payment successful');
      enableButtons([]);
    } else {
      let paymentStatusMessage = statusMessage;
      try {
        const addData =
          'additionalData' in payment ? payment.additionalData : '{}';
        const parsedAdditionalData = JSON.parse(addData);
        const { responseMessage } = parsedAdditionalData;
        if (responseMessage && responseMessage.length) {
          paymentStatusMessage = responseMessage;
        }
      } catch {
        // eslint-disable-next-line no-console
        console.warn(
          'Adyen response does not have additional data. Returning statusMessage',
        );
      }
      updateMessage(`Current payment status: ${paymentStatusMessage}`);

      if (statusMessage === 'Busy') {
        enableButtons([
          FUNC_BUTTONS.TERMINAL_DIAGNOSIS,
          FUNC_BUTTONS.CHECK_STATUS,
          FUNC_BUTTONS.CANCEL,
        ]);
        return false;
      }
      enableButtons([
        FUNC_BUTTONS.TERMINAL_DIAGNOSIS,
        FUNC_BUTTONS.RETRY,
        FUNC_BUTTONS.CLOSE,
      ]);
      return false;
    }
  } catch (error) {
    enableButtons([FUNC_BUTTONS.CHECK_STATUS]);
    updateMessage(error.message);
    displayWarning(error.message);
    return false;
  }
  window.removeEventListener('offline', offlineListener, true);
  window.removeEventListener('offline', onlineListener, true);
  return true;
};

export default adyenPayment;
